import { useEffect, useState, useRef, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import { Order } from '../types';
import { useAuth } from '../contexts/AuthContext';

type ConnectionStatus = 'connected' | 'connecting' | 'disconnected';

interface UseSignalROptions {
  onOrderCreated?: (order: Order) => void;
  onOrderUpdated?: (order: Order) => void;
  onOrderDeleted?: (data: { id: string }) => void;
  onError?: (error: Error) => void;
}

export function useSignalR({ onOrderCreated, onOrderUpdated, onOrderDeleted, onError }: UseSignalROptions = {}) {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const lastSyncTimeRef = useRef<Date>(new Date());
  const { isAuthenticated, isLoading } = useAuth();
  
  // Usar refs para los callbacks para evitar recrear la conexión
  const onOrderCreatedRef = useRef(onOrderCreated);
  const onOrderUpdatedRef = useRef(onOrderUpdated);
  const onOrderDeletedRef = useRef(onOrderDeleted);
  const onErrorRef = useRef(onError);
  
  // Actualizar refs cuando cambien los callbacks
  useEffect(() => {
    onOrderCreatedRef.current = onOrderCreated;
    onOrderUpdatedRef.current = onOrderUpdated;
    onOrderDeletedRef.current = onOrderDeleted;
    onErrorRef.current = onError;
  }, [onOrderCreated, onOrderUpdated, onOrderDeleted, onError]);

  const syncMissedOrders = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return;

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/workorders?dateFrom=${lastSyncTimeRef.current.toISOString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const missedOrders: Order[] = await response.json();
        if (missedOrders.length > 0) {
          console.log(`📥 Sincronizadas ${missedOrders.length} comandas perdidas`);
          missedOrders.forEach(order => onOrderCreatedRef.current?.(order));
        }
      }

      lastSyncTimeRef.current = new Date();
    } catch (error) {
      console.error('❌ Error al sincronizar comandas perdidas:', error);
      onErrorRef.current?.(error as Error);
    }
  }, []); // Sin dependencias porque usamos refs

  useEffect(() => {
    // No conectar hasta que termine de cargar la autenticación
    if (isLoading) {
      console.log('⏳ Esperando carga de autenticación...');
      return;
    }

    // No conectar si no está autenticado
    if (!isAuthenticated) {
      console.log('⚠️ Usuario no autenticado, no se conecta a SignalR');
      setConnectionStatus('disconnected');
      return;
    }

    const token = localStorage.getItem('accessToken');
    if (!token) {
      console.warn('⚠️ No hay token JWT en localStorage');
      setConnectionStatus('disconnected');
      return;
    }

    console.log('🔑 Token JWT encontrado, iniciando conexión SignalR...');

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${import.meta.env.VITE_API_URL}/hubs/workorders`, {
        accessTokenFactory: () => {
          // Obtener el token dinámicamente en cada llamada
          const currentToken = localStorage.getItem('accessToken');
          console.log('🔑 Obteniendo token para SignalR:', currentToken ? 'Token presente' : 'Token ausente');
          return currentToken || '';
        }
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: retryContext => {
          if (retryContext.previousRetryCount === 0) return 0;
          if (retryContext.previousRetryCount === 1) return 2000;
          if (retryContext.previousRetryCount === 2) return 10000;
          return 30000;
        }
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    connectionRef.current = connection;

    // Escuchar evento OrderCreated
    connection.on('OrderCreated', (workOrder: Order) => {
      console.log('🆕 Nueva comanda creada:', workOrder);
      onOrderCreatedRef.current?.(workOrder);
      lastSyncTimeRef.current = new Date();
    });

    // Escuchar evento OrderUpdated
    connection.on('OrderUpdated', (workOrder: Order) => {
      console.log('✏️ Comanda actualizada:', workOrder);
      onOrderUpdatedRef.current?.(workOrder);
      lastSyncTimeRef.current = new Date();
    });

    // Escuchar evento OrderStatusChanged
    connection.on('OrderStatusChanged', (workOrder: Order) => {
      console.log('🔄 Estado cambiado:', workOrder);
      onOrderUpdatedRef.current?.(workOrder);
      lastSyncTimeRef.current = new Date();
    });

    // Escuchar evento OrderDeleted
    connection.on('OrderDeleted', (data: { id: string }) => {
      console.log('🗑️ Comanda eliminada:', data.id);
      onOrderDeletedRef.current?.(data);
      lastSyncTimeRef.current = new Date();
    });

    // Manejar reconexión
    connection.onreconnected(async (connectionId) => {
      console.log('🔄 Reconectado con ID:', connectionId);
      setConnectionStatus('connected');
      await syncMissedOrders();
    });

    connection.onreconnecting((error) => {
      console.log('⚠️ Reconectando...', error);
      setConnectionStatus('connecting');
    });

    connection.onclose((error) => {
      console.log('🔴 Conexión cerrada', error);
      setConnectionStatus('disconnected');

      // Verificar si es error de autenticación
      if (error?.message?.includes('401') || error?.message?.includes('Unauthorized')) {
        console.error('🔐 Token JWT expirado o inválido');
        onErrorRef.current?.(new Error('Token de autenticación inválido'));
      }
    });

    // Iniciar conexión
    async function startConnection() {
      try {
        setConnectionStatus('connecting');
        await connection.start();
        console.log('✅ Conectado a WorkOrders Hub');
        setConnectionStatus('connected');
        lastSyncTimeRef.current = new Date();
      } catch (err) {
        console.error('❌ Error al conectar a SignalR:', err);
        setConnectionStatus('disconnected');
        onErrorRef.current?.(err as Error);
        // Reintentar después de 5 segundos
        setTimeout(startConnection, 5000);
      }
    }

    startConnection();

    // Cleanup al desmontar
    return () => {
      if (connection.state === signalR.HubConnectionState.Connected) {
        connection.stop();
        console.log('🛑 Conexión SignalR cerrada');
      }
    };
  }, [isAuthenticated, isLoading, syncMissedOrders]); // Solo dependencias estables

  return {
    connectionStatus,
    isConnected: connectionStatus === 'connected'
  };
}
